import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IOverlayLoaderStore } from '../../../../../../modules/common/OverlayLoader.store';
import { Loader } from '../../../../../components/Loader';
import { ToolTypeIcon } from '../../../../../components/ToolTypeIcon';
import { useLoader } from '../../../../../helpers/useLoader';
import { AnnotationToolType } from '../../../../annotation/submodules/annotationTypes/models/annotationToolType';
import { IProjectDetailsStatisticsBl } from '../projectDetailsStatistics.bl';
import { ToolStatisticsViewMode } from '../projectDetailsStatistics.model';

export const ToolsStatisticsSidebarContainer = observer((): ReactElement => {
  const projectDetailsStatisticsBl = useInjection(IProjectDetailsStatisticsBl.$);
  const overlayLoader = useInjection(IOverlayLoaderStore.$);
  const { t } = useTranslator();
  const { withLoaderAsync } = useLoader();
  const { projectId } = useParams<{ projectId: string }>();

  const fetchStatistics = async (): Promise<void> => {
    await withLoaderAsync(async () => {
      projectDetailsStatisticsBl.resetToolStatistics();
      await projectDetailsStatisticsBl.getProjectToolStatisticsAsync(projectId);
      if (projectDetailsStatisticsBl.store.toolStatistics.headers.annotationToolsHeaders.length > 0) {
        selectAllAnnotationType();
      } else if (projectDetailsStatisticsBl.store.toolStatistics.headers.annotationQuestionHeaders.length > 0) {
        selectAllQuestions();
      } else if (projectDetailsStatisticsBl.store.toolStatistics.headers.imageSetsHeaders.length > 0) {
        selectAllImageSets();
      }
    }, 'tool-statistics-loading');
  };

  useEffect(() => {
    void fetchStatistics();
  }, [projectId]);

  const selectAnnotationType = (annotationTypeId: string): void => {
    projectDetailsStatisticsBl.statisticsSelectAnnotationType(annotationTypeId);
  };

  const selectQuestion = (questionId: string): void => {
    projectDetailsStatisticsBl.statisticsSelectQuestion(questionId);
  };

  const selectImageSet = (imageSetId: string): void => {
    projectDetailsStatisticsBl.statisticsSelectImageSet(imageSetId);
  };

  const selectAllAnnotationType = (): void => {
    projectDetailsStatisticsBl.statisticsSelectAllAnnotationType();
  };

  const selectAllQuestions = (): void => {
    projectDetailsStatisticsBl.statisticsSelectAllQuestions();
  };

  const selectAllImageSets = (): void => {
    projectDetailsStatisticsBl.statisticsSelectAllImageSets();
  };

  const store = projectDetailsStatisticsBl.store;
  const statisticHeaders = store.toolStatistics.headers;
  const toolStatisticsViewMode = store.toolStatisticsViewMode;
  const selectedId = store.selectedToolStatistics?.annotationStatusStatistics.id;
  const loading = overlayLoader.isSpinnerVisible('tool-statistics-loading');

  return (
    <div className='sidebar'>
      <Loader isLoading={loading} />
      {statisticHeaders.annotationToolsHeaders.length > 0 && <div className='tool-statistics-sidebar-header'>{t('tools')}</div>}
      <div className='list-group'>
        {statisticHeaders.annotationToolsHeaders.length > 0 && (
          <button
            type='button'
            className={`list-group-item list-group-item-action ${toolStatisticsViewMode === ToolStatisticsViewMode.All_TOOLS ? 'active' : ''}`}
            onClick={selectAllAnnotationType}
          >
            {t('all_tools')} ({statisticHeaders.annotationToolsHeaders.map((x) => x.usageCount).reduce((a, b) => a + b, 0)})
          </button>
        )}
        {statisticHeaders.annotationToolsHeaders
          .slice()
          .sort((x, y) => x.order - y.order)
          .map((at) => (
            <button
              key={at.id}
              type='button'
              className={`list-group-item list-group-item-action indented-item ${selectedId === at.id ? 'active' : ''}`}
              onClick={(): void => {
                selectAnnotationType(at.id);
              }}
            >
              <ToolTypeIcon toolType={at.type as AnnotationToolType} /> {at.name} ({at.usageCount})
            </button>
          ))}
      </div>

      {statisticHeaders.annotationQuestionHeaders.length > 0 && <div className='tool-statistics-sidebar-header'>{t('questions')}</div>}
      <div className='list-group'>
        {statisticHeaders.annotationQuestionHeaders.length > 0 && (
          <button
            type='button'
            className={`list-group-item list-group-item-action ${toolStatisticsViewMode === ToolStatisticsViewMode.ALL_QUESTIONS ? 'active' : ''}`}
            onClick={selectAllQuestions}
          >
            {t('all_questions')} ({statisticHeaders.annotationQuestionHeaders.map((x) => x.usageCount).reduce((a, b) => a + b, 0)})
          </button>
        )}
        {statisticHeaders.annotationQuestionHeaders
          .slice()
          .sort((x, y) => x.order - y.order)
          .map((q) => (
            <button
              key={q.id}
              type='button'
              className={`list-group-item list-group-item-action indented-item ${selectedId === q.id ? 'active' : ''}`}
              onClick={(): void => {
                selectQuestion(q.id);
              }}
            >
              {q.name} ({q.usageCount})
            </button>
          ))}
      </div>
      {statisticHeaders.imageSetsHeaders.length > 0 && <div className='tool-statistics-sidebar-header'>{t('sets')}</div>}
      <div className='list-group'>
        {statisticHeaders.imageSetsHeaders.length > 0 && (
          <button
            type='button'
            className={`list-group-item list-group-item-action ${toolStatisticsViewMode === ToolStatisticsViewMode.ALL_IMAGE_SETS ? 'active' : ''}`}
            onClick={selectAllImageSets}
          >
            {t('all_image_sets')} ({statisticHeaders.imageSetsHeaders.map((x) => x.count).reduce((a, b) => a + b, 0)})
          </button>
        )}
        {statisticHeaders.imageSetsHeaders.slice().map((is) => (
          <button
            key={is.id}
            type='button'
            className={`list-group-item list-group-item-action indented-item ${selectedId === is.id ? 'active' : ''}`}
            onClick={(): void => {
              selectImageSet(is.id);
            }}
          >
            {t(`image_set_options.${is.name}`)} ({is.count})
          </button>
        ))}
      </div>
    </div>
  );
});
